import {
  useAcceptInvitation,
  usePendingInvitations,
} from '@my/directory-api/hooks';
import type { PendingInvitation } from '@my/directory-api';
import { showToast } from '@my/ui';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Post-login modal that surfaces any pending account-membership
 * invitations the caller has and lets them accept (or dismiss for the
 * session). Mounted near the top of the authenticated tree on the
 * AppShell so it fires regardless of the current route.
 *
 * V1 behaviour:
 *  - Always-modal — the prompt fires whenever there is at least one
 *    pending invitation. Dismissing hides it for the current React
 *    session; a hard refresh re-prompts. Accepting removes the row
 *    from the prompt list (and the cache) immediately.
 *  - No Decline action — for V1, declining requires an admin to remove
 *    the membership. A self-decline endpoint can be added later. The
 *    user's only other affordance here is "Dismiss for now".
 *  - One mutation in flight at a time — simpler UX while we iterate.
 *
 * Locale strings live under `organisation.pendingInvitations.*`.
 */
export function PendingInvitationsPrompt() {
  const { t } = useTranslation('organisation');
  const query = usePendingInvitations();
  const acceptMutation = useAcceptInvitation();
  const [dismissed, setDismissed] = useState(false);

  // Loading / error: render nothing — this prompt is a "best effort"
  // surface, not a blocking call. If the BE is down, the user shouldn't
  // see a broken modal; they'll see the prompt next time the query
  // succeeds.
  if (!query.data || query.data.length === 0) return null;
  if (dismissed) return null;
  if (typeof document === 'undefined') return null;

  const invitations = query.data;

  const handleAccept = async (inv: PendingInvitation) => {
    try {
      await acceptMutation.mutateAsync({ accountUserId: inv.accountUserId });
      showToast({
        type: 'success',
        message: t('pendingInvitations.accepted', { account: inv.accountName }),
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('pendingInvitations.acceptFailed'),
      });
    }
  };

  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      // Backdrop clicks do NOT dismiss — accepting or explicitly dismissing
      // is the only exit. Avoids accidental closes when the user is reaching
      // for a button.
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pending-invitations-title"
        className="w-full max-w-md rounded-md bg-white p-5 shadow-xl"
      >
        <h3
          id="pending-invitations-title"
          className="mb-1 text-base font-semibold text-slate-900"
        >
          {t('pendingInvitations.title', { count: invitations.length })}
        </h3>
        <p className="mb-4 text-xs text-slate-500">
          {t('pendingInvitations.subtitle')}
        </p>

        <ul className="space-y-2">
          {invitations.map((inv) => (
            <li
              key={inv.accountUserId}
              className="flex items-start gap-3 rounded-sm border border-slate-200 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {inv.accountName}
                </p>
                <p className="truncate text-xs text-slate-500">{inv.accountDomain}</p>
                {inv.appRoles.length > 0 && (
                  <p className="mt-1 truncate text-xs text-slate-600">
                    {inv.appRoles
                      .map((r) =>
                        t('pendingInvitations.appRoleLine', {
                          role: t(`roles.platformRoles.${r.role}`, {
                            defaultValue: r.role,
                          }),
                          app: t(`pendingInvitations.apps.${r.app}`, {
                            defaultValue: r.app,
                          }),
                        }),
                      )
                      .join(' · ')}
                  </p>
                )}
                {inv.defaultOrganisationName && (
                  <p className="truncate text-xs text-slate-500">
                    {t('pendingInvitations.organisationLine', {
                      name: inv.defaultOrganisationName,
                    })}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleAccept(inv)}
                disabled={acceptMutation.isPending}
                className="flex items-center gap-1 rounded-sm bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                <Check className="h-3.5 w-3.5" />
                {t('pendingInvitations.accept')}
              </button>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={acceptMutation.isPending}
            className="flex items-center gap-1 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
            {t('pendingInvitations.dismiss')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

